<template>
  <button
    type="button"
    :class="['halloween-generic-button', { icon: iconOnly }, props.variant]"
    :disabled="props.disabled"
    @click="onClick"
  >
    <span v-if="$slots.icon" class="halloween-generic-button-icon">
      <slot name="icon" />
    </span>
    <span class="halloween-generic-button-text">
      <slot />
    </span>
  </button>
</template>

<script setup lang="ts">
const slots = useSlots();
const props = defineProps<{
  iconOnly?: boolean;
  variant?: 'default' | 'embedded';
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: 'click', ev: MouseEvent): void;
}>();

const iconOnly = computed(() => props.iconOnly || !slots.default);

function onClick(ev: MouseEvent) {
  if (props.disabled) return;
  emit('click', ev);
}
</script>

<style scoped>
.halloween-generic-button-text {
  position: relative;
  display: block;
  z-index: 2;
  font-size: 18px;
  font-weight: 700;
  color: inherit;
  transition: inherit;
  font-family:
    PT Serif,
    serif;
}

.halloween-generic-button {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;

  padding: 8px 22px;

  background: radial-gradient(98.59% 98.53% at 51.79% 1.47%, #0083ad 0%, rgba(0, 0, 0, 0) 100%) #0d242f;
  opacity: 0.85;
  border: 2px solid #335968;
  backdrop-filter: blur(30px);
  border-radius: 21px;

  transition: 200ms ease all;
  cursor: pointer;

  color: #9ec7d4;

  &[disabled] {
    background: radial-gradient(98.59% 98.53% at 51.79% 1.47%, #b1b1b1 0%, rgba(172, 172, 172, 0) 100%), #686868;
    opacity: 0.85;
    border: 2px solid #1e353e;
    backdrop-filter: blur(30px);
    cursor: not-allowed;
    color: #d4d4d4;
  }

  &:not([disabled])::before {
    content: '';
    position: absolute;
    display: block;
    width: calc(100% + 6px);
    height: calc(100% + 6px);
    top: -3px;
    left: -3px;

    transition: inherit;
    border-radius: 21px;

    background: radial-gradient(98.59% 98.53% at 51.79% 1.47%, #0083ad 0%, rgba(0, 0, 0, 0) 100%),
      linear-gradient(0deg, rgba(13, 36, 47, 0.75), rgba(13, 36, 47, 0.75)), #ffffff;
    opacity: 0;
    border: 3px solid #608897;
    pointer-events: none;
  }
}

.halloween-generic-button.icon {
  padding: 5.5px;
}

.halloween-generic-button:not([disabled]):hover {
  backdrop-filter: blur(30px);
  border-radius: 21px;
  color: white;

  &::before {
    opacity: 1;
  }

  .halloween-generic-button-text {
    color: inherit;
  }
}

.halloween-generic-button:not([disabled]):active {
  transform: scale(0.96);
}

.halloween-generic-button-icon {
  width: 30px;
  height: 30px;
  position: relative;
  display: grid;
  place-items: center;
}

.halloween-generic-button.embedded {
  background: linear-gradient(360deg, #01080a 0%, #153b47 100%);
  border: 1px solid rgba(255, 119, 0, 0.4);
  box-shadow:
    -1px 0 3px rgba(0, 0, 0, 0.25),
    1px 2px 3px rgba(0, 0, 0, 0.25);
  border-radius: 5px;

  color: #ff7700;
  padding: 6px;

  &:not([disabled])::before {
    content: '';
    position: absolute;
    display: block;
    width: calc(100% + 6px);
    height: calc(100% + 6px);
    top: -3px;
    left: -3px;

    transition: inherit;
    border-radius: 5px;

    background: radial-gradient(98.59% 98.53% at 51.79% 1.47%, #0083ad 0%, rgba(0, 0, 0, 0) 100%),
      linear-gradient(0deg, rgba(13, 36, 47, 0.75), rgba(13, 36, 47, 0.75)), #ffffff;
    opacity: 0;
    border: 2px solid #ff7700;
    pointer-events: none;
  }

  &:hover {
    color: #ff7700;
    border-radius: 5px;
    &::before {
      opacity: 1;
    }
  }
}

.halloween-generic-button.embedded .halloween-generic-button-icon {
  width: 20px;
  height: 20px;
}
</style>
